import React, { useMemo } from "react";
import { Drawer } from "antd";
import { observer } from "mobx-react-lite";
import { Hooks } from "@xt/xtbase";
import store from "store";
import useCoinMemo from "@/components/Pages/Trade/_hook/useCoinMemo";
import useCfdConfig from "@/components/Pages/Cfd/_hook/useCfdConfig";
import XtSvg from "@/components/Xt/Svg";
import styles from "./index.module.scss";

const { useTranslation } = Hooks;

const EMPTY = "--";

/** 从配置或 exts 取值，无则返回 "--" */
function orEmpty(value: string | number | undefined | null, suffix = ""): string {
  if (value == null || value === "") return EMPTY;
  const s = typeof value === "number" ? String(value) : String(value).trim();
  if (!s) return EMPTY;
  return suffix ? `${s}${suffix}` : s;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const TradingRulesDrawer: React.FC<Props> = ({ open, onClose }) => {
  const t = useTranslation();
  const { name, currentConfig } = store.market;
  const { coinQuantityUpperCase, coinPriceUpperCase, coinPricePrecisionMarket } = useCoinMemo();
  const cfdConfig = useCfdConfig();

  const symbolDisplay = useMemo(() => {
    const base = typeof store.market.formatCfdName === "function"
      ? store.market.formatCfdName(name || "btc_usdt")
      : (name || "btc_usdt").replace(/_cfd$/i, "").replace("_", "/").toUpperCase();
    return `${base} ${t("cfd.tradingRules.contract")}`;
  }, [name, t]);

  const pricePrecisionStr = useMemo(() => {
    const n = coinPricePrecisionMarket != null && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2;
    return (10 ** -n).toFixed(n);
  }, [coinPricePrecisionMarket]);

  const overnightFeeStr = useMemo(() => {
    const r = cfdConfig.keepFeeRatio;
    if (r == null || Number.isNaN(r)) return EMPTY;
    const percent = r <= 1 && r > 0 ? r * 100 : r;
    return `${percent.toFixed(2)}%`;
  }, [cfdConfig.keepFeeRatio]);

  const maxLeverageStr = useMemo(() => {
    const max = cfdConfig.leverageRange?.max;
    if (max == null) return EMPTY;
    return `${max}`;
  }, [cfdConfig.leverageRange?.max]);

  const maxTakeProfitStr = useMemo(() => {
    const max = cfdConfig.tpSlRange?.max;
    if (max == null) return EMPTY;
    const n = typeof max === "number" ? max : parseFloat(String(max));
    if (Number.isNaN(n)) return EMPTY;
    return `${(n * 100).toFixed(2)}%`;
  }, [cfdConfig.tpSlRange?.max]);

  const maxStopLossStr = useMemo(() => {
    const min = cfdConfig.tpSlRange?.min;
    if (min == null) return EMPTY;
    const n = typeof min === "number" ? min : parseFloat(String(min));
    if (Number.isNaN(n)) return EMPTY;
    return `${(n * 100).toFixed(2)}%`;
  }, [cfdConfig.tpSlRange?.min]);

  const percentStr = (v: number | string | undefined | null): string => {
    if (v == null) return EMPTY;
    const n = typeof v === "number" ? v : parseFloat(String(v));
    if (Number.isNaN(n)) return EMPTY;
    return n <= 1 && n > 0 ? `${(n * 100).toFixed(2)}%` : `${n}%`;
  };
  const marketBuyDeviationStr = useMemo(() => percentStr(cfdConfig.marketBuyMaxDeviation), [cfdConfig.marketBuyMaxDeviation]);
  const limitOrderDeviationStr = useMemo(() => percentStr(cfdConfig.limitOrderMaxDeviation), [cfdConfig.limitOrderMaxDeviation]);
  const priceMaxDeviationStr = useMemo(() => percentStr(cfdConfig.priceMaxDeviation), [cfdConfig.priceMaxDeviation]);

  const pendingFeeStr = useMemo(() => {
    const v = cfdConfig.pendingOrderFee;
    if (v == null) return EMPTY;
    const n = typeof v === "number" ? v : parseFloat(String(v));
    if (Number.isNaN(n)) return EMPTY;
    return n <= 1 && n > 0 ? `${(n * 100).toFixed(2)}%` : `${n}%`;
  }, [cfdConfig.pendingOrderFee]);

  const rows = useMemo(() => [
    { labelKey: "cfd.tradingRules.underlying", value: symbolDisplay },
    { labelKey: "cfd.tradingRules.pricePrecision", value: pricePrecisionStr },
    { labelKey: "cfd.tradingRules.buyOrderMaxDeviation", value: marketBuyDeviationStr },
    { labelKey: "cfd.tradingRules.limitOrderMaxDeviation", value: limitOrderDeviationStr },
    { labelKey: "cfd.tradingRules.priceMaxDeviation", value: priceMaxDeviationStr },
    { labelKey: "cfd.tradingRules.minOrderQty", value: orEmpty(cfdConfig.minOrderQty, ` ${coinQuantityUpperCase}`) },
    { labelKey: "cfd.tradingRules.maxOrderQty", value: orEmpty(cfdConfig.maxOrderQty, ` ${coinQuantityUpperCase}`) },
    { labelKey: "cfd.tradingRules.minOrderAmount", value: orEmpty(cfdConfig.minOrderAmount, ` ${coinPriceUpperCase}`) },
    { labelKey: "cfd.tradingRules.maxLeverage", value: maxLeverageStr },
    { labelKey: "cfd.tradingRules.maxStopLoss", value: maxStopLossStr },
    { labelKey: "cfd.tradingRules.maxTakeProfit", value: maxTakeProfitStr },
    { labelKey: "cfd.tradingRules.overnightFee", value: overnightFeeStr },
    { labelKey: "cfd.tradingRules.settlementCurrency", value: coinPriceUpperCase || EMPTY },
    { labelKey: "cfd.tradingRules.pendingOrderFee", value: pendingFeeStr },
  ], [
    symbolDisplay,
    pricePrecisionStr,
    marketBuyDeviationStr,
    limitOrderDeviationStr,
    priceMaxDeviationStr,
    cfdConfig.minOrderQty,
    cfdConfig.maxOrderQty,
    cfdConfig.minOrderAmount,
    maxLeverageStr,
    maxStopLossStr,
    maxTakeProfitStr,
    overnightFeeStr,
    coinPriceUpperCase,
    coinQuantityUpperCase,
    pendingFeeStr,
  ]);

  return (
    <Drawer
      className={styles.drawer}
      open={open}
      onClose={onClose}
      placement="bottom"
      height="75vh"
      title={null}
      closable={false}
      contentWrapperStyle={{ maxHeight: "85vh" }}
      bodyStyle={{ paddingTop: 8, paddingBottom: 24 }}
    >
      <div className={styles.content}>
        <div className={styles.titleRow}>
          <div className={styles.title}>{t("cfd.tradingRules.title")}</div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="close">
            <XtSvg icon="close" />
          </button>
        </div>
        <div className={styles.list}>
          {rows.map((row) => (
            <div key={row.labelKey} className={styles.row}>
              <span className={styles.label}>{t(row.labelKey)}</span>
              <span className={styles.value}>{row.value}</span>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
};

export default observer(TradingRulesDrawer);
